import { AlertColor } from "@mui/material";
import type { TaskRow } from "./TasksGrid";

// The tasks API expects the assignee as "Last, First".
export const formatAssigneeName = (a?: { firstName?: string | null; lastName?: string | null; userEmail?: string | null }): string | undefined => {
  if (!a) return undefined;
  const first = (a.firstName || "").trim();
  const last = (a.lastName || "").trim();
  const name = first && last ? `${last}, ${first}` : first || last;
  return name || a.userEmail || undefined;
};

export type TaskUpdateResultItem = {
  id: number;
  status?: string;
  assignedEmployeeNumber?: string;
  assigned?: string;
  assignedEmail?: string;
};

export type TaskUpdateErrorItem = {
  id: number;
  errorMessage: string;
};

// Shape returned by createSitesClientApi.taxportal_UpdateTask
export type TaskUpdateApiResponse = {
  success?: boolean;
  message?: string;
  data?: {
    success?: boolean;
    message?: string;
    data?: TaskUpdateResultItem[];
    errors?: TaskUpdateErrorItem[];
    statusCode?: number;
  };
};

export type ProcessedTaskUpdateResult = {
  updatedRows: { row: TaskRow; result: TaskUpdateResultItem }[];
  failedRows: { row: TaskRow; errorMessage: string }[];
  successMessages: string[];
  errorMessages: string[];
};

/**
 * Matches each returned task result/error (by numeric task id) to the row it belongs to.
 * Ids not present in the current grid are ignored, per requirement to leave unmatched records alone.
 */
export function processTaskUpdateResponse(
  response: TaskUpdateApiResponse | null | undefined,
  rows: TaskRow[],
): ProcessedTaskUpdateResult {
  const resultData = response?.data?.data ?? [];
  const errors = response?.data?.errors ?? [];
  const findRow = (id: number) => rows.find((r) => r.numericTaskId === id);

  const updatedRows: { row: TaskRow; result: TaskUpdateResultItem }[] = [];
  const successMessages: string[] = [];
  for (const item of resultData) {
    const row = findRow(item.id);
    if (!row) continue;
    updatedRows.push({ row, result: item });
    successMessages.push(`${row.task || `Task ${item.id}`} updated successfully`);
  }

  const failedRows: { row: TaskRow; errorMessage: string }[] = [];
  const errorMessages: string[] = [];
  for (const err of errors) {
    const row = findRow(err.id);
    const taskName = row?.task || `Task ${err.id}`;
    errorMessages.push(`${taskName}: ${err.errorMessage}`);
    if (row) failedRows.push({ row, errorMessage: err.errorMessage });
  }

  return { updatedRows, failedRows, successMessages, errorMessages };
}

/** Builds the status/assignedTo display overrides for a row from its update result. */
export function buildRowOverridesFromResult(
  result: TaskUpdateResultItem,
): { status?: string; assignedTo?: string; assignedEmail?: string } {
  const patch: { status?: string; assignedTo?: string; assignedEmail?: string } = {};
  if (result.status) patch.status = result.status;
  // The API response is the source of truth for assignees; do not merge with previous values.
  if (result.assigned) {
    patch.assignedTo = result.assigned;
    patch.assignedEmail = result.assignedEmail ?? "";
  }
  return patch;
}

/** Shows success/failure results as two separate sequential snackbars (only one can be on
 * screen at a time, so the failure one is deferred until the success one auto-hides). */
export function showTaskUpdateSnackbar(
  showSnackbar: (args: { message: string; severity: AlertColor }) => void,
  successMessages: string[],
  errorMessages: string[],
): void {
  if (successMessages.length === 0 && errorMessages.length === 0) return;

  if (successMessages.length > 0 && errorMessages.length > 0) {
    showSnackbar({ message: successMessages.join("\n"), severity: "success" });
    setTimeout(() => {
      showSnackbar({ message: errorMessages.join("\n"), severity: "error" });
    }, 3700);
    return;
  }

  if (successMessages.length > 0) {
    showSnackbar({ message: successMessages.join("\n"), severity: "success" });
    return;
  }

  showSnackbar({ message: errorMessages.join("\n"), severity: "error" });
}
